"use client";

import type { Metadata } from "next";
import React, { useEffect, useState } from "react";
import { activityApi } from "@/lib/api/services/activity.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  Users,
  Globe,
  Zap,
  Activity,
  TrendingUp,
} from "lucide-react";
import Link from "next/link";

const metadata: Metadata = {
  title: "About HRShakti — Our Mission & Community",
  description:
    "Learn about HRShakti — the verified, community-first ecosystem for HR professionals. Building the world's most comprehensive professional platform for Human Resources.",
  keywords: ["about HRShakti", "HR community", "HR professionals", "HR platform", "HR mission"],
  openGraph: {
    title: "About HRShakti — Our Mission & Community",
    description:
      "Learn about HRShakti — the verified, community-first ecosystem for HR professionals.",
    type: "website",
    url: "https://hrshakti.com/about",
    siteName: "HRShakti",
  },
  twitter: {
    card: "summary_large_image",
    title: "About HRShakti — Our Mission & Community",
    description:
      "Learn about HRShakti — the verified, community-first ecosystem for HR professionals.",
  },
  robots: { index: true, follow: true },
  alternates: { canonical: "https://hrshakti.com/about" },
};

interface Stats {
  totalMembers: number;
  activeDiscussions: number;
  eventsHosted: number;
  coursesAvailable: number;
}

export default function AboutPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await activityApi.getStats();
        const s = (data as any).data ?? null;
        setStats({
          totalMembers: s?.totalMembers ?? 0,
          activeDiscussions: s?.activeDiscussions ?? 0,
          eventsHosted: s?.eventsHosted ?? 0,
          coursesAvailable: s?.coursesAvailable ?? 0,
        });
      } catch {
        setStats(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const values = [
    {
      icon: Shield,
      title: "Trust",
      description: "We maintain a verified, safe community for HR professionals.",
    },
    {
      icon: Users,
      title: "Community",
      description: "Connect with peers, share knowledge, and grow together.",
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "A worldwide network of HR professionals across industries.",
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "AI-powered tools and modern workflows for HR excellence.",
    },
  ];

  return (
    <main className="mx-auto max-w-4xl px-4 py-16">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground">About HRShakti</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Building the world&apos;s most comprehensive professional platform for Human Resources.
        </p>
      </div>

      {isLoading ? (
        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-10 w-10 bg-muted/10 rounded animate-pulse" />
                <div className="mt-4 h-6 w-1/3 bg-muted/10 rounded animate-pulse" />
                <div className="mt-2 h-4 w-2/3 bg-muted/10 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : stats ? (
        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {values.map((value) => (
            <Card key={value.title}>
              <CardContent className="p-6">
                <value.icon className="h-10 w-10 text-primary" />
                <h3 className="mt-4 text-xl font-semibold text-foreground">
                  {value.title}
                </h3>
                <p className="mt-2 text-muted-foreground">{value.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {values.map((value) => (
            <Card key={value.title}>
              <CardContent className="p-6">
                <value.icon className="h-10 w-10 text-primary" />
                <h3 className="mt-4 text-xl font-semibold text-foreground">
                  {value.title}
                </h3>
                <p className="mt-2 text-muted-foreground">{value.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-16 text-center">
        <h2 className="text-2xl font-bold text-foreground">Our Mission</h2>
        <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          HRShakti exists to empower HR professionals with the knowledge, connections,
          and tools they need to drive organizational success.
        </p>
      </div>

      {stats && (
        <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-4">
          <Card>
            <CardContent className="p-6 text-center">
              <TrendingUp className="mx-auto h-8 w-8 text-primary" />
              <p className="mt-2 text-2xl font-bold text-foreground">{stats.totalMembers.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Members</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Activity className="mx-auto h-8 w-8 text-primary" />
              <p className="mt-2 text-2xl font-bold text-foreground">{stats.activeDiscussions.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Active Discussions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Zap className="mx-auto h-8 w-8 text-primary" />
              <p className="mt-2 text-2xl font-bold text-foreground">{stats.eventsHosted}</p>
              <p className="text-sm text-muted-foreground">Events Hosted</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Globe className="mx-auto h-8 w-8 text-primary" />
              <p className="mt-2 text-2xl font-bold text-foreground">{stats.coursesAvailable}</p>
              <p className="text-sm text-muted-foreground">Courses Available</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mt-12 text-center">
        <Button asChild>
          <Link href="/auth/register">Join Our Community</Link>
        </Button>
      </div>
    </main>
  );
}